import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import type { Script, ScriptForm } from "@/features/dashboard/types";
import { useDashboardDialogs } from "./useDashboardDialogs";

export const useDashboardDialogOrchestration = (scripts: Script[]) => {
  const {
    openShareDialog,
    closeShareDialog,
    shareDialogState,
    openManageParticipants,
    closeManageParticipants,
    manageParticipantsState,
    openAnalysisDialog,
    closeAnalysisDialog,
    selectedScriptForm,
    showAddScript,
    openAddScript,
    closeAddScript,
    showSettings,
    openSettings,
    closeSettings,
    setShareDialogState,
  } = useDashboardDialogs();

  const [searchParams, setSearchParams] = useSearchParams();
  const analysisToken = searchParams.get("analysis");

  const updateAnalysisParam = useCallback((value: string | null) => {
    const params = new URLSearchParams(searchParams.toString());
    if (value) {
      params.set("analysis", value);
    } else {
      params.delete("analysis");
    }

    if (params.toString() === searchParams.toString()) return;
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (analysisToken && scripts.length > 0 && !selectedScriptForm) {
      const foundScriptForm = scripts
        .flatMap((script) => script.script_forms.map((form) => ({ script, form })))
        .find(({ form }) => form.review_link === analysisToken);

      if (foundScriptForm) {
        openAnalysisDialog(foundScriptForm.form, foundScriptForm.script.movie_name, foundScriptForm.script);
      }
    }
  }, [analysisToken, scripts, openAnalysisDialog, selectedScriptForm]);

  useEffect(() => {
    if (selectedScriptForm) {
      updateAnalysisParam(selectedScriptForm.scriptForm.review_link);
    }
  }, [selectedScriptForm, updateAnalysisParam]);

  const handleShareScript = useCallback((script: Script, scriptForm: ScriptForm) => {
    openShareDialog(script, scriptForm);
  }, [openShareDialog]);

  const handleManageParticipants = useCallback((script: Script, scriptForm: ScriptForm) => {
    openManageParticipants({ scriptForm, scriptName: script.movie_name });
  }, [openManageParticipants]);

  const handleAnalyze = useCallback((scriptForm: ScriptForm, scriptName: string, script: Script) => {
    openAnalysisDialog(scriptForm, scriptName, script);
  }, [openAnalysisDialog]);

  const handleAnalysisDialogClose = useCallback(() => {
    closeAnalysisDialog();
    updateAnalysisParam(null);
  }, [closeAnalysisDialog, updateAnalysisParam]);

  return {
    showAddScript,
    openAddScript,
    closeAddScript,
    showSettings,
    openSettings,
    closeSettings,
    shareDialogState,
    openShareDialog,
    closeShareDialog,
    setShareDialogState,
    manageParticipantsState,
    openManageParticipants,
    closeManageParticipants,
    selectedScriptForm,
    handleShareScript,
    handleManageParticipants,
    handleAnalyze,
    handleAnalysisDialogClose,
  } as const;
};
